/**
 * DIEUKYDIEU.TV - STORY DETAIL VIEW MODULE
 */
import { t } from './i18n.js';
import { navigateToView } from './router.js';
import { findArticleData, getArticleFormattedDate, getArticleViews, generateStoryContent, formatArticleBody } from './modules/story-content.js';
import { renderStoryCompanionWidgets, renderStoryHashtags, renderStoryRelatedArticles, toggleStoryAudioPlayback, copyStoryBookmarkQuote } from './modules/story-companion.js';
import {
    currentStoryArticle,
    setCurrentStoryArticle,
    getArticleLikes,
    setStoryFontSizeLevel,
    updateAllStoryLikeButtons,
    ensureCurrentStoryArticle,
    handleStoryLikeToggle,
    openStoryShare,
    shareStoryToChannel,
    copyStoryLink,
    openStoryQrCode,
    seeMoreCategoryStories,
    changeStoryFontSize,
    resetStoryFontSize,
    applyStoryFontSize,
    scrollToStoryTop
} from './modules/story-actions.js';

export {
    currentStoryArticle,
    setCurrentStoryArticle,
    findArticleData,
    renderStoryCompanionWidgets,
    toggleStoryAudioPlayback,
    copyStoryBookmarkQuote,
    updateAllStoryLikeButtons,
    ensureCurrentStoryArticle,
    handleStoryLikeToggle,
    openStoryShare,
    shareStoryToChannel,
    copyStoryLink,
    openStoryQrCode,
    seeMoreCategoryStories,
    changeStoryFontSize,
    resetStoryFontSize,
    scrollToStoryTop
};

export async function openStoryBySlugOrId(slugOrId, pushHistory = true) {
    if (!slugOrId) return;
    const clean = String(slugOrId).trim();

    // 1. Try finding in in-memory sections / articles first
    const found = findArticleData(clean, !isNaN(Number(clean)) ? Number(clean) : null);
    if (found && found.article) {
        openStoryDetail(found.article, null, null, null, null, null, pushHistory);
        return;
    }

    // 2. Fetch from backend API
    try {
        const { sitemapApi } = await import('./api/sitemap.api.js');
        const art = await sitemapApi.getArticleById(slugOrId);
        const resolved = (art && art.data) ? art.data : art;
        if (resolved && (resolved.id || resolved.title)) {
            openStoryDetail(resolved, null, null, null, null, null, pushHistory);
            return;
        }
    } catch (err) {
        console.warn('[story] Failed fetching article by slug/id:', err);
    }

    // 3. Fallback to default article 101
    openStoryDetail(101, null, null, null, null, null, pushHistory);
}

export function openStoryDetail(titleOrArticle, imgUrl, desc, tag, duration, id, pushHistory = true) {
    let art = null;
    let category = null;

    if (typeof titleOrArticle === 'object' && titleOrArticle !== null) {
        art = { ...titleOrArticle };
        const found = findArticleData(art, art.id);
        category = found.category || art.category || null;
    } else {
        const found = findArticleData(titleOrArticle, id);
        if (found.article) {
            art = { ...found.article };
            category = found.category || art.category || null;
        } else {
            const articleId = id || (typeof titleOrArticle === 'number' ? titleOrArticle : Date.now());
            art = {
                id: articleId,
                title: typeof titleOrArticle === 'string' ? titleOrArticle : 'Bài viết chi tiết',
                img: imgUrl || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=600&q=80',
                desc: desc || '',
                tag: tag || 'Khám phá',
                duration: duration || ''
            };
        }
    }

    setCurrentStoryArticle(art);
    if (typeof window !== 'undefined' && typeof window.recordReadingHistory === 'function') {
        window.recordReadingHistory(art);
    }

    // Render all elements
    renderStoryContent(art, category);

    // Switch view without generic hash
    navigateToView('story-detail-view', { pushHistory: false });

    // Update URL to /kham-pha/:slug for clean SEO sharing
    const slugOrId = art.slug || art.id;
    const cleanUrl = `/kham-pha/${slugOrId}`;
    if (pushHistory) {
        window.history.pushState({ viewId: 'story-detail-view', slug: slugOrId }, '', cleanUrl);
    } else if (window.location.pathname !== cleanUrl && !window.location.hash) {
        window.history.replaceState({ viewId: 'story-detail-view', slug: slugOrId }, '', cleanUrl);
    }
    if (art.title) {
        document.title = `${art.title} - dieukydieu.tv`;
    }

    // Scroll to top instantly
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Initialize reading progress bar
    const progressBar = document.getElementById('reading-progress-bar');
    if (progressBar) {
        progressBar.classList.add('active');
        progressBar.style.width = '0%';
    }

    // Sync likes across top and bottom like buttons
    updateAllStoryLikeButtons(art.id);
}

/**
 * Render all DOM components inside #story-detail-view
 */
function renderStoryContent(art, category) {
    const container = document.getElementById('story-detail-view');
    if (!container) return;

    // Breadcrumbs
    const catBreadcrumb = document.getElementById('story-breadcrumb-cat');
    const tagBreadcrumb = document.getElementById('story-breadcrumb-tag');
    const catName = category ? category.name : 'Khám phá';
    if (catBreadcrumb) {
        catBreadcrumb.innerText = catName;
        catBreadcrumb.onclick = () => {
            if (category && category.slug && typeof window.openCategoryBySlug === 'function') {
                window.openCategoryBySlug(category.slug);
            } else {
                navigateToView('category-view');
            }
        };
    }
    if (tagBreadcrumb) tagBreadcrumb.innerText = art.tag || 'Chi tiết';

    // Badges above title
    const catChipName = document.getElementById('story-category-name');
    const subtagChip = document.getElementById('story-subtag-chip');
    if (catChipName) catChipName.innerText = catName;
    if (subtagChip) subtagChip.innerText = art.tag || 'Khám phá';

    // Title
    const titleEl = document.getElementById('story-title-el');
    if (titleEl) titleEl.innerText = art.title;

    // Meta stats
    const dateVal = document.getElementById('story-date-val');
    if (dateVal) dateVal.innerText = art.date || getArticleFormattedDate(art.id);

    // Banner image
    const bannerEl = document.getElementById('story-banner-el');
    if (bannerEl) bannerEl.style.backgroundImage = `url('${art.img}')`;

    // Sapo text
    const sapoEl = document.getElementById('story-sapo-text');
    if (sapoEl) sapoEl.innerText = art.desc || 'Những câu chuyện và bài học ý nghĩa về cuộc sống.';

    // Story body
    const bodyEl = document.getElementById('story-body-el');
    if (bodyEl) {
        bodyEl.innerHTML = formatArticleBody(art.content) || generateStoryContent(art, category);
        applyStoryFontSize();
    }

    // Author & Source signature at bottom of article
    const authorSigEl = document.getElementById('story-author-signature');
    if (authorSigEl) {
        let author = art.author || 'Ban Biên Tập';
        let source = art.source || 'Tân Thế Kỷ';
        let sourceUrl = art.sourceUrl || 'https://tantheky.com';

        if (art.id === 901 || (art.title && art.title.includes('Vì sao có nhân loại'))) {
            author = 'Đại Sư Lý Hồng Chí';
            source = 'DKN.TV';
            sourceUrl = 'https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-co-nhan-loai.html';
        }

        authorSigEl.innerHTML = `<strong class="signature-author">${author}</strong> <em class="signature-source">(Theo <a href="${sourceUrl}" target="_blank" rel="noopener noreferrer">${source}</a>)</em>`;
    }

    // Hashtags, related stories, and companion widgets
    renderStoryHashtags(art, category);
    renderStoryRelatedArticles(art, category);
    renderStoryCompanionWidgets(art);
    bindStoryActionListeners(art);
}

function bindStoryActionListeners(art) {
    const bindClick = (id, fn) => {
        const el = document.getElementById(id);
        if (el) el.onclick = fn;
    };
    bindClick('story-top-like-btn', (e) => handleStoryLikeToggle(e));
    bindClick('story-top-share-btn', (e) => openStoryShare(e));
    bindClick('story-bottom-like-btn', (e) => handleStoryLikeToggle(e));
    bindClick('share-channel-fb', () => shareStoryToChannel('facebook'));
    bindClick('share-channel-zalo', () => shareStoryToChannel('zalo'));
    bindClick('share-channel-copy', () => copyStoryLink());
    bindClick('share-channel-qr', () => openStoryQrCode());
    bindClick('see-more-cat-btn', () => seeMoreCategoryStories());
    bindClick('font-decrease-btn', () => changeStoryFontSize(-1));
    bindClick('font-reset-btn', () => resetStoryFontSize());
    bindClick('font-increase-btn', () => changeStoryFontSize(1));
}

/**
 * Font Size Controls
 */

export function initStoryDetailModule() {
    try {
        const savedLevel = parseInt(localStorage.getItem('dieukydieu_font_level'));
        if (!isNaN(savedLevel)) {
            setStoryFontSizeLevel(savedLevel);
        }
    } catch (e) {}

    const art = ensureCurrentStoryArticle();
    const found = findArticleData(art, art.id);
    renderStoryContent(art, found.category);
    applyStoryFontSize();
    updateAllStoryLikeButtons(art.id);
    bindStoryActionListeners(art);
}
